import { useEffect, useRef, useState } from 'react';
import { useHomeData } from '@/hooks/useTranslatedData';

export default function TestimonialsSection() {
  const testimonialsData = useHomeData().testimonials;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const firstRow = testimonialsData.list.slice(0, 3);
  const secondRow = testimonialsData.list.slice(3);

  const avatars = [
    'https://readdy.ai/api/search-image?query=Young%20professional%20Asian%20woman%20smiling%20headshot%20portrait%2C%20clean%20minimal%20background%2C%20natural%20soft%20lighting%2C%20modern%20corporate%20style%2C%20warm%20and%20friendly%20expression%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=11&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Young%20athletic%20Asian%20man%20with%20healthy%20glow%20headshot%20portrait%2C%20minimal%20clean%20background%2C%20natural%20sunlight%20lighting%2C%20wellness%20lifestyle%20aesthetic%2C%20confident%20friendly%20smile%2C%20high%20quality%20portrait&width=80&height=80&seq=12&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Young%20Asian%20female%20student%20with%20glasses%20headshot%20portrait%2C%20clean%20white%20background%2C%20natural%20soft%20lighting%2C%20academic%20intellectual%20vibe%2C%20warm%20genuine%20smile%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=13&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Muscular%20athletic%20Asian%20man%20headshot%20portrait%2C%20clean%20minimal%20background%2C%20strong%20confident%20expression%2C%20fitness%20lifestyle%20aesthetic%2C%20natural%20lighting%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=14&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Creative%20young%20Asian%20woman%20artist%20headshot%20portrait%2C%20minimal%20aesthetic%20background%2C%20soft%20diffused%20natural%20lighting%2C%20artistic%20creative%20vibe%2C%20warm%20approachable%20smile%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=15&orientation=squarish',
  ];

  return (
    <section ref={sectionRef} className="bg-brand-sand py-16 md:py-24">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-flask-line text-brand-mint-dark text-lg"></i>
            </div>
            <span className="text-xs font-semibold tracking-wider text-brand-gray uppercase">
              {testimonialsData.sectionLabel}
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal">
            {testimonialsData.sectionTitle}
          </h2>
        </div>

        {/* Testimonials Grid - First Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-5 md:mb-6">
          {firstRow.map((item, index) => (
            <div
              key={item.name}
              className={`bg-white rounded-2xl p-6 md:p-8 transition-all duration-700 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* User Info */}
              <div className="flex items-center gap-3 mb-5">
                <img
                  src={avatars[index]}
                  alt={item.name}
                  className="w-11 h-11 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <span className="block text-sm font-semibold text-brand-charcoal">
                    {item.name}
                  </span>
                  <span className="block text-xs text-brand-gray-light">
                    {item.role}
                  </span>
                </div>
              </div>

              {/* Quote icon */}
              <div className="mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-2xl text-brand-mint"></i>
                </div>
              </div>

              {/* Content */}
              <p className="text-sm text-brand-stone leading-relaxed">
                {item.content}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonials Grid - Second Row (2 cards) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {secondRow.map((item, index) => (
            <div
              key={item.name}
              className={`bg-white rounded-2xl p-6 md:p-8 transition-all duration-700 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index + 3) * 150}ms` }}
            >
              <div className="flex items-center gap-3 mb-5">
                <img
                  src={avatars[index + 3]}
                  alt={item.name}
                  className="w-11 h-11 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <span className="block text-sm font-semibold text-brand-charcoal">
                    {item.name}
                  </span>
                  <span className="block text-xs text-brand-gray-light">
                    {item.role}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-double-quotes-l text-2xl text-brand-coral"></i>
                </div>
              </div>

              <p className="text-sm text-brand-stone leading-relaxed">
                {item.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}